import * as React from 'react'
import { AlertCircle, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert'
import { Button } from '../../../components/ui/button'
import { Separator } from '../../../components/ui/separator'
import { cn } from '../../../lib/utils'
import { MappingSkipConfirmDialog } from './MappingSkipConfirmDialog'
import { scopeList } from './plan'
import { WizardChrome } from './WizardChrome'
import type { IngestWizardApi } from './useIngestWizard'

interface StepReviewProps {
  wizard: IngestWizardApi
}

export function StepReview({ wizard }: StepReviewProps) {
  const { state, issues, planClean, backTo2, goStep3, fixIssue, waiveIssue } = wizard
  const scope = scopeList(state)
  const blocking = issues.filter((iss) => (iss.severity ?? 'error') === 'error')
  const warnings = issues.filter((iss) => iss.severity === 'warning')
  const missingMappings = warnings
    .map((warning) => warning.fixSourceId)
    .filter(
      (id): id is 'ipmap' | 'roboticsmap' | 'safetymap' | 'tagmap' =>
        id === 'ipmap' || id === 'roboticsmap' || id === 'safetymap' || id === 'tagmap'
    )
  const [confirmSkipOpen, setConfirmSkipOpen] = React.useState(false)

  const requestContinue = () => {
    if (missingMappings.length > 0) {
      setConfirmSkipOpen(true)
      return
    }
    goStep3()
  }

  return (
    <WizardChrome
      eyebrow="Step 02 — Review"
      title={
        blocking.length > 0
          ? 'A few things need attention'
          : warnings.length > 0
            ? 'Scope looks good — with recommendations'
            : 'Scope looks good'
      }
      description={
        blocking.length > 0
          ? 'Resolve or waive each blocking item before collecting files and connection details.'
          : 'Uploads and ingest order are set up in the next step. Recommended mappings are optional.'
      }
      onBack={backTo2}
      onContinue={requestContinue}
      continueDisabled={!planClean}
      continueLabel="Continue"
    >
      <div className="flex flex-col gap-2.5">
        {blocking.map((iss) => (
          <Alert key={iss.id} variant="destructive" className="border-l-[3px] border-l-st-down">
            <AlertCircle className="size-4" />
            <AlertTitle>{iss.title}</AlertTitle>
            <AlertDescription>
              <p>{iss.detail}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-brand bg-brand-soft text-brand hover:bg-brand-soft/70 hover:text-brand"
                  onClick={() => fixIssue(iss.id, iss.fixSourceId)}
                >
                  {iss.fixLabel}
                </Button>
                {iss.canWaive ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => waiveIssue(iss.id)}
                  >
                    {iss.waiveLabel ?? 'Continue anyway'}
                  </Button>
                ) : null}
              </div>
            </AlertDescription>
          </Alert>
        ))}

        {warnings.map((iss) => (
          <Alert
            key={iss.id}
            className={cn(
              'border-l-[3px] border-l-st-warn border-st-warn/30 bg-st-warn-bg text-st-warn'
            )}
          >
            <AlertTriangle className="size-4" />
            <AlertTitle className="text-st-warn">{iss.title}</AlertTitle>
            <AlertDescription className="text-st-warn/90">
              <p>{iss.detail}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-st-warn/40 bg-bg-elev text-st-warn hover:bg-bg-elev/80"
                  onClick={() => fixIssue(iss.id, iss.fixSourceId)}
                >
                  {iss.fixLabel}
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        ))}

        {planClean ? (
          <div className="rounded-lg border border-line bg-bg-elev p-4">
            <div className="flex gap-2.5">
              <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-st-run" />
              <div>
                <div className="text-14 font-medium text-ink">Ready to prepare uploads</div>
                <div className="mt-1 text-13 leading-snug text-ink-3">
                  {warnings.length > 0
                    ? 'Companion mappings are recommended but not required to continue.'
                    : 'Selected sources are ready for file and connection details.'}
                </div>
              </div>
            </div>
            <Separator className="my-3.5" />
            <div className="flex flex-col gap-1.5">
              {scope.map((row) => (
                <div key={row.label} className="flex items-baseline gap-2.5 text-13">
                  <span className="min-w-0 flex-1 text-ink">{row.label}</span>
                  <span className="font-mono text-11 text-ink-3">{row.detail}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
      <MappingSkipConfirmDialog
        open={confirmSkipOpen}
        onOpenChange={setConfirmSkipOpen}
        mappings={missingMappings}
        onConfirm={() => {
          setConfirmSkipOpen(false)
          goStep3()
        }}
      />
    </WizardChrome>
  )
}
